import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, Truck, ShieldCheck, ArrowLeft } from "lucide-react";
import { api, formatINR } from "@/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({});
  const [related, setRelated] = useState([]);
  const [recos, setRecos] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setQty(1);
    setSelected({});
    api.get(`/products/${slug}`)
      .then(({ data }) => {
        if (data.group === "equipment") {
          navigate(`/equipment/product/${data.slug}`, { replace: true });
          return;
        }
        setProduct(data);
        const defaults = {};
        Object.entries(data.variants || {}).forEach(([k, v]) => { if (v.length === 1) defaults[k] = v[0]; });
        setSelected(defaults);
        api.get("/products", { params: { group: "shop", category: data.category_slug } }).then((r) =>
          setRelated(r.data.filter((p) => p.slug !== data.slug).slice(0, 4))
        );
        api.get(`/products/${data.slug}/recommendations`).then((r) => setRecos(r.data)).catch(() => setRecos([]));
      })
      .catch(() => setNotFound(true));
  }, [slug, navigate]);

  if (notFound) {
    return (
      <div className="container-ping py-32 text-center" data-testid="product-not-found">
        <h1 className="heading-2">Product Not Found</h1>
        <Link to="/shop" className="mt-6 inline-block text-[#E31837] label-caps">Back to Shop</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-ping py-20 grid md:grid-cols-2 gap-12">
        <div className="aspect-square bg-secondary animate-pulse" />
        <div className="space-y-4">
          <div className="h-8 w-2/3 bg-secondary animate-pulse" />
          <div className="h-6 w-1/3 bg-secondary animate-pulse" />
          <div className="h-24 bg-secondary animate-pulse" />
        </div>
      </div>
    );
  }

  const variantKeys = Object.keys(product.variants || {});
  const allSelected = variantKeys.every((k) => selected[k]);
  const colourKey = variantKeys.find((k) => /colou?r/i.test(k));
  const selectedColour = colourKey ? selected[colourKey] : null;
  const displayImage = (selectedColour && product.variant_images?.[selectedColour]) || product.image;

  const handleAdd = () => {
    if (!allSelected) {
      toast.error("Please select all options");
      return;
    }
    const variantStr = variantKeys.map((k) => `${k}: ${selected[k]}`).join(" / ") || null;
    addItem(product, qty, variantStr);
  };

  return (
    <div data-testid="product-detail-page">
      <div className="container-ping pt-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 label-caps text-muted-foreground hover:text-[#0A0A0A] btn-transition" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      </div>
      <div className="container-ping py-8 grid md:grid-cols-2 gap-12">
        <div className="md:sticky md:top-28 self-start">
          <div className="relative bg-[#0A0A0A] aspect-square overflow-hidden border border-border">
            {product.tag && (
              <span className="absolute top-4 left-4 z-10 label-caps px-3 py-1 bg-[#E31837] text-white">{product.tag}</span>
            )}
            <img src={displayImage} alt={product.name} className="w-full h-full knockout-white" style={{ objectFit: product.image_fit || "cover", objectPosition: product.image_position || "center" }} data-testid="product-image" />
          </div>
        </div>

        <div>
          <p className="label-caps text-[#E31837]">{product.category}</p>
          <h1 className="heading-2 mt-2" data-testid="product-name">{product.name}</h1>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-3xl font-bold" data-testid="product-price">{formatINR(product.price)}</span>
            {product.old_price && (
              <span className="text-lg text-muted-foreground line-through">{formatINR(product.old_price)}</span>
            )}
          </div>
          <p className="mt-6 text-muted-foreground leading-relaxed">{product.description}</p>

          {/* Variant selectors */}
          {variantKeys.map((k) => (
            <div key={k} className="mt-6" data-testid={`variant-group-${k.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="label-caps text-muted-foreground mb-2">{k}</p>
              <div className="flex flex-wrap gap-2">
                {product.variants[k].map((v) => (
                  <button
                    key={v}
                    onClick={() => setSelected((s) => ({ ...s, [k]: v }))}
                    className={`px-4 py-2 border text-sm font-semibold btn-transition ${selected[k] === v ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-border hover:border-[#0A0A0A]"}`}
                    data-testid={`variant-${k.toLowerCase().replace(/\s/g, "-")}-${v.replace(/[^a-zA-Z0-9]/g, "")}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-8 flex items-stretch gap-4">
            <div className="flex items-center border border-[#0A0A0A]">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-4 hover:bg-secondary btn-transition" data-testid="qty-decrease" aria-label="Decrease">
                <Minus className="h-4 w-4" />
              </button>
              <span className="px-5 font-bold w-12 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-4 py-4 hover:bg-secondary btn-transition" data-testid="qty-increase" aria-label="Increase">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={handleAdd}
              className="flex-1 bg-[#E31837] text-white label-caps flex items-center justify-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-[0.98]"
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag className="h-5 w-5" /> Add To Bag
            </button>
          </div>

          <div className="mt-6 flex flex-col gap-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#E31837]" /> Dispatched in 2–4 business days</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#E31837]" /> 100% genuine PING products</span>
          </div>

          <Accordion type="single" collapsible defaultValue="specs" className="mt-8">
            <AccordionItem value="specs">
              <AccordionTrigger className="label-caps" data-testid="specs-trigger">Product Details</AccordionTrigger>
              <AccordionContent>
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(product.specs || {}).map(([k, v]) => (
                      <tr key={k} className="border-b border-border">
                        <td className="py-3 font-semibold uppercase text-xs tracking-wider text-muted-foreground w-1/3">{k}</td>
                        <td className="py-3">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping">
              <AccordionTrigger className="label-caps">Shipping & Returns</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Dispatched within 2–4 business days across India. 7-day returns on unused items in original packaging.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {related.length > 0 && (
        <section className="bg-[#F4F4F5] py-16 mt-8">
          <div className="container-ping">
            <h2 className="heading-3 mb-8">You May Also Like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {recos.length > 0 && (
        <section className="bg-white py-16 border-t border-border" data-testid="recommendations">
          <div className="container-ping">
            <p className="label-caps text-[#E31837] mb-2">Complete The Look</p>
            <h2 className="heading-3 mb-8">Golfers Also Add</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {recos.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
